/**
 * Authentication Context
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuthenticatedUser,
  getUserAttributes,
  getUserRoles,
  signOutUser,
  getIdToken
} from '../services/authService';

const AuthContext = createContext(null);

/**
 * Hook to use authentication context
 * @returns {Object} Authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods to children
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load current user and their attributes
   */
  const loadUser = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentUser = await getAuthenticatedUser();
      
      if (currentUser) {
        setUser(currentUser);

        // Fetch user attributes with error handling
        try {
          const attributes = await getUserAttributes();
          setUserAttributes(attributes);
        } catch (attrErr) {
          console.warn('Could not fetch user attributes:', attrErr.message);
          // Don't fail the whole auth flow if attributes fail
          setUserAttributes({});
        }

        // Fetch user roles with error handling
        try {
          const userRoles = await getUserRoles();
          setRoles(userRoles);
        } catch (roleErr) {
          console.warn('Could not fetch user roles:', roleErr.message);
          setRoles([]);
        }
      } else {
        setUser(null);
        setUserAttributes(null);
        setRoles([]);
      }
    } catch (err) {
      console.error('Error loading user:', err);
      
      // Only set error for critical failures, not rate limiting
      if (!err.message?.includes('Rate exceeded')) {
        setError(err.message);
      }
      
      setUser(null);
      setUserAttributes(null);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out current user
   */
  const signOut = async () => {
    try {
      await signOutUser();
      setUser(null);
      setUserAttributes(null);
      setRoles([]);
    } catch (err) {
      console.error('Error signing out:', err);
      throw err;
    }
  };

  /**
   * Refresh user session and attributes
   */
  const refreshUser = async () => {
    await loadUser();
  };

  /**
   * Get JWT token for API requests
   */
  const getToken = async () => {
    try {
      return await getIdToken();
    } catch (err) {
      console.error('Error getting token:', err);
      return null;
    }
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    return roles.includes(role);
  };

  /**
   * Check if user is administrator
   */
  const isAdmin = () => {
    return hasRole('administrator');
  };

  /**
   * Check if user is speaker
   */
  const isSpeaker = () => {
    return hasRole('speaker');
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = () => {
    return !!user;
  };

  /**
   * Get user display name
   */
  const getDisplayName = () => {
    if (userAttributes?.['custom:display_name']) {
      return userAttributes['custom:display_name'];
    }
    if (userAttributes?.email) {
      return userAttributes.email;
    }
    return 'User';
  };

  /**
   * Get user email
   */
  const getEmail = () => {
    return userAttributes?.email || '';
  };

  /**
   * Get user ID (sub claim)
   */
  const getUserId = () => {
    return user?.userId || user?.username || '';
  };

  /**
   * Get profile photo URL
   */
  const getProfilePhotoUrl = () => {
    return userAttributes?.['custom:profile_photo_url'] || null;
  };

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  const value = {
    user,
    userAttributes,
    roles,
    loading,
    error,
    signOut,
    refreshUser,
    getToken,
    hasRole,
    isAdmin,
    isSpeaker,
    isAuthenticated,
    getDisplayName,
    getEmail,
    getUserId,
    getProfilePhotoUrl
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
